/**
 * Physical inventory HTTP client — PI-1 settings only.
 */
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export type FetchResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; error: string };

export type PiOrgSettingsDto = {
  organization_id: string;
  enabled: boolean;
  cadence: "monthly" | "weekly" | "none";
  due_day_of_month: string;
  grace_days: number;
  notify_days_before: number;
  assignee_mode: "location_members" | "org_wide_sales";
  photos_required: number;
  photo_slot_1_label: string;
  photo_slot_2_label: string;
  photo_slot_3_label: string;
  require_serial_readable: boolean;
  created_at: string;
  updated_at: string;
};

async function piFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    /* non-JSON */
  }
  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err };
  }
  return { ok: true, status: r.status, data: json as T };
}

export async function getPiOrgSettings(): Promise<FetchResult<{ ok: true; settings: PiOrgSettingsDto }>> {
  return piFetch("/api/pi/settings");
}

export async function patchPiOrgSettings(
  patch: Partial<PiOrgSettingsDto>
): Promise<FetchResult<{ ok: true; settings: PiOrgSettingsDto }>> {
  return piFetch("/api/pi/settings/patch", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}
